import { Share } from 'react-native'
import { router } from 'expo-router'

import { useMutation, useQuery } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import { GAME_CONSTANTS } from '@ppwr/convex/shared/constants'

import { useStore as useMobXStore } from '@/models'
import { create } from 'zustand'
import { events } from '@/models/analytics/analytics-events'
import { formatPlayDate, formatIsoDateString } from '@/utils/dateFormatter'
import { formatMs} from '@/utils/timeFormatter'

import { useThrottledCallback } from 'use-debounce'

const { GAME_NAME, NAVIGATION_THROTTLE_MS, ROUND_COUNT } = GAME_CONSTANTS.CTH_CONSTANTS

import { defaultTheme, purpleTheme, greenTheme, defaultImageTheme, purpleImageTheme, greenImageTheme } from "@/components/Mini/theme"

type StoreState = {
  GAME_NAME: string
  ROUND_COUNT: number
  playedDate: string
  setPlayedDate: () => void
  theme: typeof defaultTheme
  setTheme: (themeName: string) => void
}

const useGameStore = create<StoreState>((set, get) => ({
  GAME_NAME,
  ROUND_COUNT,
  playedDate: formatPlayDate(),
  setPlayedDate: () => set({ playedDate: formatPlayDate() }),
  theme: purpleImageTheme,
  setTheme: (themeName) => {
    if (themeName === 'default') {
      set({ theme: defaultTheme})
    } else if (themeName === 'purple'){
      set({ theme: purpleTheme })
    } else if (themeName === 'green'){
      set({ theme: greenTheme })
    } else if (themeName == 'defaultImage'){
      set({theme: defaultImageTheme})
    } else if (themeName == 'purpleImage'){
      set({theme: purpleImageTheme})
    } else if (themeName == 'greenImage'){
      set({theme: greenImageTheme})
    }
  }
}))


 function useStore<T>(selector: (s: StoreState) => T): T {
  return useGameStore(selector)
}



 function useCthUser(){
  
  const { session: { user, isAdmin, isSuperAdmin }, analytics: { setEvent } } = useMobXStore()
  
  return {
    userId: user?.auth0Id ?? null,
    userName: user?.name ?? null,
    isAdmin,
    isSuperAdmin,
    setEvent
  }
}

function useCthQueryStatus(){

  const { userId } = useCthUser()
  const playedDate = useStore(state => state.playedDate)

  const status = useQuery(api.cth.queries.getCthStatus, userId ? { userId, playedDate } : 'skip')

  return {status};

}

function useCthQueryLeaderboard(){
    
  const playedDate = useStore(state => state.playedDate)

  const leaderboard = useQuery(api.cth.queries.getCthLeaderboard, { playedDate })

  return {leaderboard};

}

function useCthQueryLog(){
    
  const { userId } = useCthUser()

  const log = useQuery(api.cth.queries.getCthHistory, userId ? { userId } : 'skip')

  return {log};

}

function useCthQueryHistory(){

  const { userId } = useCthUser()
  const playedDate = useStore(state => state.playedDate)

  const stats = useQuery(api.cth.queries.getCthProfileStats, userId ? { userId, todayDate: playedDate } : 'skip')

  return { stats }

}

function useCthQueryDiamonds(){
  const category="cth"
  const playedDate = useStore((state) => state.playedDate)
  const diamondEvents = useQuery(api.diamonds.queries.getDiamondEvents, {
    category,
    refId: playedDate
  });

  return {
    events: diamondEvents?.events,
    diamonds: diamondEvents?.total ?? 0
  }
}


//admin handlers
function useHandleTestingReset(){

  const { userId } = useCthUser()
  const playedDate = useStore(state => state.playedDate)
  const resetCthGame = useMutation(api.cth.mutations.resetCthGameForTesting)
  
  const handleTestingReset = async () => {
    if (!userId) return
    await resetCthGame({ userId, playedDate })
  }

  return handleTestingReset
  
}

function useHandleSeedLeaderboard(){

  const playedDate = useStore(state => state.playedDate)
  const seedLeaderboard = useMutation(api.cth.mutations.seedCthLeaderboard)

  const handleSeedLeaderboard = async () => {
    await seedLeaderboard({ count: 20, playedDate })
  }

  return handleSeedLeaderboard

}

function useHandleClearLeaderboardSeeds(){

  const playedDate = useStore(state => state.playedDate)
  const clearLeaderboardSeeds = useMutation(api.cth.mutations.clearCthLeaderboardSeedData)

  const handleClearLeaderboardSeeds = async () => {
    await clearLeaderboardSeeds({ playedDate })
  }

  return handleClearLeaderboardSeeds

}

//nav handlers 

function useHandleModule({ event }: { event?: string } = {}){
  
  const { userId, setEvent } = useCthUser()
  const playedDate = useStore(state => state.playedDate)

  const handleModule = useThrottledCallback(() => {
    
    if (event){
      setEvent(event, {
        userId,
        playedDate
      })
    }

    router.push('/(root)/cth')

  }, NAVIGATION_THROTTLE_MS)

  return handleModule

}

function useHandleClose(){

  const handleClose = useThrottledCallback(() => {
    router.back()
   }, NAVIGATION_THROTTLE_MS)

  return handleClose
}

function useHandleHome(){

  const handleHome = useThrottledCallback(() => {
    
    router.dismissTo('/(root)/cth')

  }, NAVIGATION_THROTTLE_MS)

  return handleHome

}

function useHandlePlay(){

  const { userId, setEvent } = useCthUser()
  const playedDate = useStore(state => state.playedDate)
  const { status } = useCthQueryStatus();

  const handlePlay = useThrottledCallback(() => {
    let event;
    if (status?.status === 'completed'){
      event = events.cth.replay_tapped;
    } else if (status?.status === 'in_progress'){
      event = events.cth.resume_tapped;
    } else { 
      event = events.cth.play_tapped;
    } 
  
    setEvent(event, {
      userId,
      playedDate
    })
    
    router.push({ pathname: '/(root)/cth/game' })
  }, NAVIGATION_THROTTLE_MS)

  return handlePlay

}

function useHandleResults(){

  const { userId, setEvent } = useCthUser()
  const playedDate = useStore(state => state.playedDate)

  const handleResults = useThrottledCallback(() => {
    setEvent(events.cth.results_tapped, {
      userId,
      playedDate
    })
    router.push({ pathname: '/(root)/cth/results' })
  }, NAVIGATION_THROTTLE_MS)

  return handleResults;
}

function useHandleLeaderboard(){

  const { userId, setEvent } = useCthUser()
  const playedDate = useStore(state => state.playedDate)

  const handleLeaderboard = useThrottledCallback(() => {
    setEvent(events.cth.leaderboard_tapped, {
      userId,
      playedDate
    })
    router.push('/(root)/cth/leaderboard')
  }, NAVIGATION_THROTTLE_MS)

  return handleLeaderboard;

}

function useHandleLog(){

  const { userId, setEvent } = useCthUser()

  const playedDate = useStore(state => state.playedDate)

  const handleLog = useThrottledCallback(() => {
    setEvent(events.cth.profile_history_tapped, {
      userId,
      playedDate
    })
    router.navigate('/(root)/cth/log')
  }, NAVIGATION_THROTTLE_MS)

  return handleLog 
}

//handlers

function useHandleShare() {
    const { userId, setEvent } = useCthUser()
    const playedDate = useStore((state) => state.playedDate)
    const gameName = useStore(s => s.GAME_NAME)
    const { status } = useCthQueryStatus()

    const handleShare = async () => {
      if (status?.status !== 'completed') return

      const { roundsCorrect, totalRounds, roundResults, totalElapsedMs } = status

      //const whiteSquare = '\u{2B1C}'
      const redSquare = '\u{1F7E5}'
      const greenSquare = '\u{1F7E9}'

      const squares = roundResults.map((r) => r.correct ? greenSquare : redSquare).join('')

      const timeStr = totalElapsedMs != null ? ` in ${formatMs(totalElapsedMs)}` : ''
      const dateStr = formatIsoDateString(playedDate)
  
      const shareText =
        `${gameName} - ${dateStr}\n` +
        `${squares}\n` +
        `Rounds: ${roundsCorrect}/${totalRounds}${timeStr}\n` +
        `\n` +
        `Play at pokerpower.com`
      
      setEvent(events.cth.results_share_tapped, {
        userId,
        playedDate,
        roundsCorrect,
        totalHands: totalRounds
      })

      try {
        await Share.share({ message: shareText })
      } catch {
        // User cancelled
      }
    }

    return handleShare
}

function useIsPerfect(){
  let isPerfect = false;

  const { status } = useCthQueryStatus()

  if (status?.status === "completed"){
    if (status?.roundsCorrect === status?.totalRounds){
      if (status?.roundsCorrect > 0){
        isPerfect = true;
      }
    }

  }
  return isPerfect;
}



export type { StoreState }
export { 
  //useGameStore,
  useStore, //zustand vars stable across pages
  useCthUser, //eventually useCurrentUser as a general hook and separate call to useStore for setEvents
  useCthQueryStatus, //reusable query with params
  useCthQueryStatus as useQueryStatus, //move these all to no namespace
  useCthQueryLeaderboard, //
  useCthQueryLog, //
  useCthQueryHistory, //
  useCthQueryDiamonds,
  useHandleTestingReset, //admin handler
  useHandleSeedLeaderboard, //
  useHandleClearLeaderboardSeeds,// 
  useHandleModule,
  useHandleClose, //nav handler
  useHandleHome, //
  useHandlePlay, //
  useHandleResults, //
  useHandleLeaderboard, //
  useHandleLog, // 
  useHandleShare, // handler
  useIsPerfect
}
